// TerraTimelineNavigator — Tier-A "Terra" renderer for the timeline-navigator
// component (the world's SECOND home-set pairing, after progressive-builder).
//
// Reuses the existing Terra toolkit registration (atmosphere + earthy audio cue
// palette in lib/scenario-themes/) — no new world infra. Reframes the vertical
// timeline as a GROWING VINE: each event is a node on a climbing stem; visiting
// (expanding) an event unfurls a leaf there (a milestone blooms a flower), and the
// stem grows in proportion to how much of the timeline you've explored.
//
// State machine + onActivityResponse mirror TimelineNavigatorSlide's Student
// exactly: `expanded` is PURELY visual (open/closed); `visited` is a separate
// MONOTONIC set (grows on first expand, never shrinks on collapse) so the vine /
// finish gate never regress. Plus `questionAnswers` + `reflection` + `submitted`.
// On submit it emits
//   { activityType:'timeline-navigator', mode:'survey',
//     responseData:{ visited:[id…], questionAnswers, reflection? } }
// (reflection only included when the author set a prompt).

import { useState, useCallback, useEffect, useRef } from 'react';
import type { TimelineNavigatorSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio, playTerraSprout } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';

const STYLE_ID = 'terra-tl-scoped-styles';

const PARCHMENT = '#f3e9d2';
const SURFACE = '#fffaf0';
const INK = '#3a2c18';
const SOIL = '#5a4326';
const LEAF = '#2f6e3b';
const LEAF_DEEP = '#21512b';
const SPROUT = '#7fb83f';
const TERRACOTTA = '#d2613c';
const HONEY = '#e6a82e';

// ─────────────────────────────────────────────────────────────────────────────
// Scoped CSS — namespaced `.terratl`; injected once, ref-counted.
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(): string {
  return `
.terratl { position: relative; --ease-grow: cubic-bezier(.22,1,.36,1); }
.terratl * { box-sizing: border-box; }

.terratl .ttl-eyebrow { text-transform: uppercase; letter-spacing: .42em; font-size: .68rem; font-weight: 600; color: ${TERRACOTTA}; }

/* the vine track */
.terratl .ttl-vine { position: relative; padding-left: 44px; }
.terratl .ttl-stem-track { position: absolute; left: 17px; top: 6px; bottom: 6px; width: 4px; border-radius: 4px; background: rgba(90,67,38,.22); overflow: hidden; }
.terratl .ttl-stem-grow { position: absolute; left: 0; top: 0; width: 100%; border-radius: 4px; background: linear-gradient(${SPROUT}, ${LEAF}); box-shadow: 0 0 8px rgba(127,184,63,.5); transition: height 1s var(--ease-grow); }

/* node markers */
.terratl .ttl-node { position: absolute; left: -36px; top: 6px; width: 30px; height: 30px; display: grid; place-items: center; }
.terratl .ttl-bud { width: 12px; height: 12px; border-radius: 50% 50% 50% 0; transform: rotate(45deg); background: rgba(90,67,38,.3); border: 1.5px solid ${SOIL}; transition: all .5s var(--ease-grow); }
.terratl .ttl-leaf-svg { width: 28px; height: 28px; opacity: 0; transform: scale(.2) rotate(-20deg); transition: opacity .5s ease, transform .6s var(--ease-grow); }
.terratl .ttl-node.visited .ttl-bud { opacity: 0; }
.terratl .ttl-node.visited .ttl-leaf-svg { opacity: 1; transform: scale(1) rotate(0); }

.terratl .ttl-row { position: relative; margin-bottom: 18px; }
.terratl .ttl-card {
  width: 100%; text-align: left; cursor: pointer; border-radius: 14px; padding: 16px 18px;
  background: linear-gradient(180deg, ${SURFACE}, ${PARCHMENT}); border: 1.5px solid rgba(90,67,38,.22);
  box-shadow: 0 10px 24px -16px rgba(90,67,38,.55); transition: transform .2s var(--ease-grow), box-shadow .2s;
}
.terratl .ttl-card:hover { transform: translateX(2px); box-shadow: 0 14px 30px -16px rgba(90,67,38,.6); }
.terratl .ttl-date { font-family: "Courier Prime", monospace; font-size: .68rem; letter-spacing: .14em; text-transform: uppercase; color: ${TERRACOTTA}; margin: 0 0 4px; }
.terratl .ttl-date .ms { color: ${HONEY}; }
.terratl .ttl-title { font-size: 1.12rem; line-height: 1.2; color: ${LEAF_DEEP}; margin: 0 0 4px; }
.terratl .ttl-summary { font-size: .9rem; color: ${SOIL}; margin: 0; line-height: 1.5; }

.terratl .ttl-detail { overflow: hidden; max-height: 0; opacity: 0; transition: max-height .6s var(--ease-grow), opacity .5s ease, margin .4s; }
.terratl .ttl-detail.open { max-height: 480px; opacity: 1; margin-top: 10px; }
.terratl .ttl-detail-inner { border-left: 4px solid ${SPROUT}; background: rgba(127,184,63,.1); border-radius: 0 10px 10px 0; padding: 14px 16px; }
.terratl .ttl-detail-inner p { font-size: .92rem; line-height: 1.6; color: ${INK}; margin: 0; }
.terratl .ttl-q { margin-top: 12px; }
.terratl .ttl-q label { display: block; font-size: .88rem; font-weight: 600; color: ${LEAF_DEEP}; margin-bottom: 6px; }
.terratl .ttl-q textarea { width: 100%; font-family: inherit; font-size: .9rem; color: ${INK}; background: ${SURFACE}; border: 1.5px solid rgba(90,67,38,.28); border-radius: 8px; padding: 9px 11px; resize: vertical; }
.terratl .ttl-q textarea:focus { outline: none; border-color: ${LEAF}; box-shadow: 0 0 0 3px rgba(47,110,59,.16); }

.terratl .ttl-reflection { margin-top: 26px; text-align: center; animation: ttlBloom .6s var(--ease-grow) both; }
@keyframes ttlBloom { from { opacity: 0; transform: translateY(16px) scale(.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
.terratl .ttl-reflection-card { display: inline-block; max-width: 600px; padding: 28px 32px; background: linear-gradient(180deg, ${SURFACE}, #ead9b6); border-radius: 20px; border: 2px solid rgba(47,110,59,.3); box-shadow: 0 22px 44px -22px rgba(47,110,59,.5); }
.terratl .ttl-reflection-card .em { font-size: 2.2rem; display: block; margin-bottom: 6px; }
.terratl .ttl-reflection-card p { color: ${SOIL}; font-size: 1rem; line-height: 1.5; margin: 0 0 16px; }
.terratl .ttl-submit { font-weight: 700; font-size: .92rem; letter-spacing: .04em; color: #fff7e6; cursor: pointer; background: linear-gradient(180deg, ${TERRACOTTA}, #b14d2c); border: none; border-radius: 999px; padding: 12px 26px; box-shadow: 0 10px 24px -10px rgba(178,77,44,.8); transition: transform .3s var(--ease-grow); }
.terratl .ttl-submit:hover { transform: translateY(-3px); }
.terratl .ttl-thanks { color: ${LEAF_DEEP}; font-size: 1.02rem; }

@media (prefers-reduced-motion: reduce) {
  .terratl .ttl-leaf-svg, .terratl .ttl-detail, .terratl .ttl-stem-grow, .terratl .ttl-reflection { transition-duration: .001s !important; animation-duration: .001s !important; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function LeafIcon({ milestone }: { milestone?: boolean }) {
  if (milestone) {
    // a small bloom for milestones
    return (
      <svg className="ttl-leaf-svg" viewBox="0 0 28 28" fill="none">
        {Array.from({ length: 6 }).map((_, i) => {
          const a = (i / 6) * Math.PI * 2;
          return <ellipse key={i} cx={14 + Math.cos(a) * 7} cy={14 + Math.sin(a) * 7} rx={4} ry={6.5} fill={i % 2 ? HONEY : TERRACOTTA} transform={`rotate(${(a * 180) / Math.PI + 90} ${14 + Math.cos(a) * 7} ${14 + Math.sin(a) * 7})`} opacity={0.95} />;
        })}
        <circle cx={14} cy={14} r={5} fill={SOIL} />
        <circle cx={12.5} cy={12.5} r={1.6} fill="#ffe9a8" />
      </svg>
    );
  }
  return (
    <svg className="ttl-leaf-svg" viewBox="0 0 28 28" fill="none">
      <path d="M14 26 C14 18 14 14 14 10" stroke={LEAF} strokeWidth={2} strokeLinecap="round" />
      <path d="M14 16 C6 16 2 12 2 5 C10 5 14 9 14 16 Z" fill={LEAF} />
      <path d="M14 12 C22 12 26 8 26 1 C18 1 14 5 14 12 Z" fill={SPROUT} />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function TerraTimelineNavigator({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: TimelineNavigatorSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const reduceMotion = usePrefersReducedMotion();
  useScopedStyles();
  const { enabled: audioOn, toggle: toggleAudio, play, playWith } = useThemeWorldAudio('terra');
  const headingFont = theme.fonts.heading;

  // ── State machine (mirrors TimelineNavigatorSlide's Student) ─────────────────
  // The {visited, questionAnswers} payload only fires on submit, so its presence
  // means the timeline finished — restore the explored/submitted view.
  const saved: Record<string, unknown> = savedResponse ?? {};
  const restored = Array.isArray(saved.visited);
  // `expanded` is purely visual; `visited` is monotonic (added on expand, never
  // removed). A rehydrated view restores every saved event as both visited AND
  // expanded so the submitted detail/answers are shown back.
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(restored ? (saved.visited as string[]) : []),
  );
  const [visited, setVisited] = useState<Set<string>>(
    () => new Set(restored ? (saved.visited as string[]) : []),
  );
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>(() =>
    restored && saved.questionAnswers && typeof saved.questionAnswers === 'object'
      ? { ...(saved.questionAnswers as Record<string, string>) }
      : {},
  );
  const [reflection, setReflection] = useState(
    restored && typeof saved.reflection === 'string' ? saved.reflection : '',
  );
  const [submitted, setSubmitted] = useState(restored);
  // Fires onComplete exactly once at the terminal submit (already done if rehydrated).
  const completedRef = useRef(restored);

  const total = data.events.length;
  // Finish is gated on visiting every event, NOT on a reflection prompt existing.
  const allVisited = total > 0 && data.events.every((e) => visited.has(e.id));
  const growPct = total > 0 ? Math.round((visited.size / total) * 100) : 0;
  const hasReflectionPrompt = (data.reflection?.prompt ?? '').trim().length > 0;

  const toggle = useCallback(
    (id: string, isMilestone?: boolean) => {
      let firstVisit = false;
      setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      // Collapsing must not un-visit — visited only ever grows.
      setVisited((prev) => {
        if (prev.has(id)) return prev;
        firstVisit = true;
        return new Set(prev).add(id);
      });
      if (firstVisit) {
        // a fresh leaf unfurls — earthy sprout (brighter step = more of the vine grown)
        playWith((e) => playTerraSprout(e, visited.size));
        if (isMilestone) play('reveal'); // a milestone blooms
      }
    },
    [play, playWith, visited],
  );

  const submit = useCallback(() => {
    setSubmitted(true);
    play('advance');
    const responseData: Record<string, unknown> = {
      visited: Array.from(visited),
      questionAnswers,
    };
    // Only include reflection when the author actually set a prompt.
    if (hasReflectionPrompt) responseData.reflection = reflection.trim();
    onActivityResponse?.({
      activityType: 'timeline-navigator',
      mode: 'survey',
      responseData,
    });
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }
  }, [visited, questionAnswers, reflection, hasReflectionPrompt, onActivityResponse, onComplete, play]);

  return (
    <div className="terratl h-full w-full overflow-y-auto" style={{ background: PARCHMENT, color: INK, fontFamily: theme.fonts.body }}>
      <ThemeWorldAtmosphere worldId="terra" primary={LEAF} accent={TERRACOTTA} background={PARCHMENT} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 2 }}>
        <div style={{ maxWidth: 720, margin: '0 auto', padding: '80px 24px 90px' }}>
          {/* header */}
          <header style={{ textAlign: 'center', marginBottom: 26 }}>
            <div className="ttl-eyebrow">Terra · A Living Timeline</div>
            <h1 style={{ fontFamily: headingFont, fontWeight: 400, fontSize: 'clamp(2rem, 5.5vw, 3.6rem)', lineHeight: 1, color: LEAF_DEEP, margin: '12px 0 8px', textShadow: '0 1px 0 rgba(255,255,255,.4)' }}>
              {data.title}
            </h1>
            {data.subtitle && <p style={{ maxWidth: 520, margin: '0 auto', fontSize: '1rem', color: SOIL, lineHeight: 1.5 }}>{data.subtitle}</p>}
            <p style={{ fontFamily: '"Courier Prime", monospace', fontSize: '0.74rem', letterSpacing: '0.1em', color: SOIL, marginTop: 14 }}>
              🌱 {visited.size} / {total} leaves unfurled
            </p>
          </header>

          {/* the vine */}
          <div className="ttl-vine">
            <div className="ttl-stem-track">
              <div className="ttl-stem-grow" style={{ height: `${growPct}%` }} />
            </div>

            {data.events.map((event) => {
              const isOpen = expanded.has(event.id);
              // The leaf is monotonic — once unfurled (visited) it stays unfurled
              // even when the card is collapsed.
              const isVisited = visited.has(event.id);
              return (
                <div key={event.id} className="ttl-row">
                  <div className={`ttl-node${isVisited ? ' visited' : ''}`}>
                    {!isVisited && <span className="ttl-bud" />}
                    <LeafIcon milestone={event.isMilestone} />
                  </div>

                  <button type="button" className="ttl-card" onClick={() => toggle(event.id, event.isMilestone)}>
                    <p className="ttl-date">
                      {event.date}
                      {event.isMilestone && <span className="ms"> · milestone</span>}
                    </p>
                    <h3 className="ttl-title" style={{ fontFamily: headingFont }}>{event.title}</h3>
                    <p className="ttl-summary">{event.summary}</p>
                  </button>

                  <div className={`ttl-detail${isOpen ? ' open' : ''}`}>
                    <div className="ttl-detail-inner">
                      <p>{event.detail}</p>
                      {event.question && (
                        <div className="ttl-q">
                          <label>{event.question}</label>
                          <textarea
                            rows={2}
                            value={questionAnswers[event.id] ?? ''}
                            disabled={submitted}
                            onChange={(e) => setQuestionAnswers((prev) => ({ ...prev, [event.id]: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Finish / reflection — the finish affordance ALWAYS renders once every
              event is visited; it is NOT gated on a reflection prompt existing.
              When the author set a prompt we show it WITH a textarea to answer in
              (and the CTA reads "Plant the record"); otherwise the same card just
              carries a "Plant the record" button so the timeline can always be
              completed and onComplete/onActivityResponse always fire. */}
          {allVisited && (
            <div className="ttl-reflection">
              <div className="ttl-reflection-card">
                <span className="em">🌳</span>
                {hasReflectionPrompt && (
                  <p style={{ fontFamily: headingFont, fontSize: '1.15rem', color: LEAF_DEEP }}>{data.reflection.prompt}</p>
                )}
                {!submitted ? (
                  <>
                    {hasReflectionPrompt && (
                      <div className="ttl-q" style={{ marginTop: 0, marginBottom: 16, textAlign: 'left' }}>
                        <textarea
                          rows={3}
                          value={reflection}
                          onChange={(e) => setReflection(e.target.value)}
                          placeholder="Your thoughts…"
                          aria-label="Reflection"
                        />
                      </div>
                    )}
                    <button type="button" className="ttl-submit" onClick={submit}>Plant the record</button>
                  </>
                ) : (
                  <p className="ttl-thanks">🌿 Fully grown. Thank you.</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
